import * as React from "react"
import { gql } from "@apollo/client"
import { Badge, Box, Flex, Image, LinkBox, LinkOverlay, Text, useColorModeValue } from "@chakra-ui/react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import { EventItemFragment } from "lib/graphql"
import { transformImage } from "lib/helpers/utils"

dayjs.extend(utc)

const _ = gql`
  fragment EventItem on Event {
    id
    name
    type
    region
    categories
    image
    date
    slug
    time
    location
    time
    maxAttendees
    attendeeCount
  }
`
import { useMe } from "lib/hooks/useMe"

interface Props {
  event: EventItemFragment
  isRecommended?: boolean
}

export function EventTile({ event, isRecommended }: Props) {
  const { me } = useMe()
  const colorGray = useColorModeValue("gray.500", "gray.400")
  const remainingSpots = event && event.maxAttendees && event?.maxAttendees - event?.attendeeCount

  const eventType = event.type || ""
  const eventRegion = event.region || ""
  const eventCategories = event.categories?.reduce((acc, i) => acc + i + ", ", "").slice(0, -2) || ""
  const eventDate = event.date ? dayjs.utc(event.date).format("DD MMM") : ""
  const eventLocation = event.location || ""
  const eventTime = event.time || ""

  // Build comprehensive aria-label for the link
  const ariaLabelParts = [
    `Evenement: ${event.name}`,
    eventType && `Type: ${eventType}`,
    eventRegion && `Regio: ${eventRegion}`,
    eventCategories && `Categorieën: ${eventCategories}`,
    eventDate && `Datum: ${eventDate}`,
    eventTime && `Tijd: ${eventTime}`,
    eventLocation && `Locatie: ${eventLocation}`,
    remainingSpots !== undefined &&
      remainingSpots !== null &&
      remainingSpots < 5 &&
      (remainingSpots === 1
        ? "Nog 1 plek beschikbaar"
        : remainingSpots <= 0
        ? "Evenement vol"
        : `Nog ${remainingSpots} plekken beschikbaar`),
  ].filter(Boolean)

  const linkAriaLabel = ariaLabelParts.join(". ")

  // Build image alt text
  const imageAlt = event?.image
    ? `Afbeelding van evenement: ${event.name}`
    : "Placeholder afbeelding voor evenement"

  return (
    <LinkBox
      position="relative"
      borderRadius="md"
      p="3px"
      _focusWithin={{
        boxShadow: "0 0 0 3px var(--chakra-colors-blue-300)",
        outline: "none",
      }}
    >
      <Box position="relative" overflow="hidden" borderRadius="md">
        <LinkOverlay
          href={`/te-doen/${event.slug}`}
          textDecoration="none"
          aria-label={linkAriaLabel}
          display="block"
          _focus={{
            outline: "none",
          }}
        >
          <Image
            src={event?.image ? transformImage(event?.image, "h_650") : "event-placeholder.png"}
            h="210px"
            w="100%"
            objectFit="cover"
            objectPosition="center"
            alt={imageAlt}
            borderRadius="md"
            loading="lazy"
          />

          <Box width="100%" py={3} px={1}>
            <Text fontWeight="bold" fontSize="2xl" textTransform="capitalize" as="h3">
              {event.name}
            </Text>
            <Text fontSize={16} color={colorGray} whiteSpace="nowrap" overflow="hidden" isTruncated>
              {eventType && `${eventType} / `} {eventRegion && `${eventRegion} / `}
              {eventCategories}
            </Text>

            <Text fontWeight="bold" fontSize={16} color="yellow.400">
              {eventDate}
              {eventTime && ` @ ${eventLocation}`}
            </Text>
          </Box>
          {!isRecommended &&
            me &&
            me?.interests.filter((interest) => event.categories.includes(interest)).length > 0 && (
              <Badge
                pos="absolute"
                top="176px"
                left={2}
                bg="yellow.500"
                color="black"
                fontSize="md"
                whiteSpace="nowrap"
                overflow="hidden"
                aria-label="Dit evenement past bij jouw interesses"
                role="status"
              >
                Iets voor jou?
              </Badge>
            )}
        </LinkOverlay>
        {remainingSpots !== undefined && remainingSpots !== null && remainingSpots < 5 ? (
          <Flex pos="absolute" top={0} left={0} w="100%" display="block" borderRadius="md" role="status" aria-live="polite">
            {remainingSpots === 1 ? (
              <Text
                fontWeight="bold"
                color="black"
                bg="pink.500"
                textAlign="center"
                fontSize="sm"
                py={1}
                borderRadius="md"
                aria-label="Nog 1 plek beschikbaar voor dit evenement"
              >
                Nog {remainingSpots} plek beschikbaar
              </Text>
            ) : remainingSpots <= 0 ? (
              <Text
                fontWeight="bold"
                color="black"
                bg="pink.500"
                textAlign="center"
                fontSize="sm"
                py={1}
                borderRadius="md"
                aria-label="Dit evenement is vol"
              >
                Evenement vol
              </Text>
            ) : (
              <Text
                fontWeight="bold"
                color="black"
                bg="pink.500"
                textAlign="center"
                fontSize="sm"
                py={1}
                borderRadius="md"
                aria-label={`Nog ${remainingSpots} plekken beschikbaar voor dit evenement`}
              >
                Nog {remainingSpots} plekken beschikbaar
              </Text>
            )}
          </Flex>
        ) : null}
      </Box>
    </LinkBox>
  )
}
